import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Plus, CalendarIcon } from 'lucide-react';
import { toast } from 'sonner';
import { eventService } from '@/services/eventService';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';

interface GuestSuggestionDialogProps {
  eventId: string;
  guestId: string;
  onSuggestionSubmitted: () => void;
}

export const GuestSuggestionDialog = ({ eventId, guestId, onSuggestionSubmitted }: GuestSuggestionDialogProps) => {
  const [open, setOpen] = useState(false);
  const [suggestionType, setSuggestionType] = useState<'date' | 'time' | 'location'>('date');
  const [suggestionValue, setSuggestionValue] = useState('');
  const [selectedDate, setSelectedDate] = useState<Date>();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const finalValue = suggestionType === 'date' && selectedDate 
      ? format(selectedDate, 'MMMM d, yyyy')
      : suggestionValue.trim();
    
    if (!finalValue) {
      toast.error('Please enter a suggestion');
      return;
    }

    setIsSubmitting(true);
    try {
      await eventService.suggestOption(eventId, guestId, {
        type: suggestionType,
        value: finalValue,
      });
      
      toast.success('Suggestion submitted! The host will review it.');
      setSuggestionValue('');
      setSelectedDate(undefined);
      setOpen(false);
      onSuggestionSubmitted();
    } catch (error) {
      console.error('Error submitting suggestion:', error);
      toast.error('Failed to submit suggestion');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="gap-2">
          <Plus className="h-4 w-4" />
          Suggest Alternative
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Suggest Alternative Option</DialogTitle>
          <DialogDescription>
            Suggest an alternative date, time, or location for this event
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="suggestion-type">Type</Label>
            <Select value={suggestionType} onValueChange={(value) => setSuggestionType(value as 'date' | 'time' | 'location')}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="date">Date</SelectItem>
                <SelectItem value="time">Time</SelectItem>
                <SelectItem value="location">Location</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="suggestion-value">Suggestion</Label>
            {suggestionType === 'date' ? (
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal",
                      !selectedDate && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {selectedDate ? format(selectedDate, "PPP") : <span>Pick a date</span>}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={selectedDate}
                    onSelect={setSelectedDate}
                    initialFocus
                    className="pointer-events-auto"
                  />
                </PopoverContent>
              </Popover>
            ) : (
              <Input
                id="suggestion-value"
                value={suggestionValue}
                onChange={(e) => setSuggestionValue(e.target.value)}
                placeholder={
                  suggestionType === 'time' ? 'e.g., 7:00 PM' :
                  'e.g., Central Park, NYC'
                }
                required
              />
            )}
          </div>
          
          <div className="flex justify-end space-x-2">
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Submitting...' : 'Submit Suggestion'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};